// @ts-strict-ignore
import {DATASET_MIXPANEL, PropertyType, ResourceType} from 'common/report/constants';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {Report} from 'common/report/insights/models/report';
import {BehaviorShowClause, FormulaShowClause} from 'common/report/insights/models/show-clause';
import {getBookmarkParamsWithDateRange} from 'common/report/insights/util/bookmark-overrides';
import {getSegmentSections} from 'common/report/insights/util/segments';
import {
  ChartPlotStyle,
  ChartType,
  ConversionWindowUnit,
  FunnelOrder,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsChartType,
  InsightsResourceType,
  MATH_TYPE,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  SubBehavior,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {QueryMode} from 'common/report/insights/util/constants';

import {FunnelsAIQuery, InsightsAIQuery, RetentionAIQuery} from './types';
import {
  getBreakdownSegments,
  getCommonEventParams,
  getDateRange,
  getLiftComparisonParam,
  getPropertyFilters,
  getTimeComparisonParam,
} from './util';

type SupportedAIQuery = FunnelsAIQuery | InsightsAIQuery | RetentionAIQuery;

const isFunnelsQuery = (aiQuery: SupportedAIQuery): aiQuery is FunnelsAIQuery => aiQuery.report.type === `funnels`;
const isRetentionQuery = (aiQuery: SupportedAIQuery): aiQuery is RetentionAIQuery =>
  aiQuery.report.type === `retention`;

function getQueryMode(aiQuery: SupportedAIQuery): QueryMode {
  if (isFunnelsQuery(aiQuery)) {
    return QueryMode.Funnels;
  } else if (isRetentionQuery(aiQuery)) {
    return QueryMode.Retention;
  }

  return QueryMode.Events;
}

/**
 * Generate an Insights bookmark from the AI query.
 */
export function aiQueryToInsightsBookmark(aiQuery: SupportedAIQuery): DisplayBookmark<InsightsBookmarkParams> {
  const report = Report.createDefaultReport({
    queryMode: getQueryMode(aiQuery),
  });

  // metrics
  if (isFunnelsQuery(aiQuery)) {
    let measurementType: MATH_TYPE = FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE;
    let measurementProperty = null;

    if (aiQuery.countType === FunnelsCountType.General) {
      measurementType = FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL;
    } else if (aiQuery.countType === FunnelsCountType.Session) {
      measurementType = FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION;
    }

    // some old chart types map to measurement types instead
    if (aiQuery.chartType === `time-to-convert`) {
      measurementType = PRIMITIVE_MATH_TYPES.AVERAGE;
      measurementProperty = {
        dataset: DATASET_MIXPANEL,
        defaultType: PropertyType.Number,
        type: PropertyType.Number,
        name: `$time_to_conversion`,
        resourceType: ResourceType.Other,
        unit: null,
      };
    }

    // metrics
    report.sections.show.clauses = [
      new BehaviorShowClause({
        behavior: {
          type: MetricType.Funnel,
          resourceType: InsightsResourceType.Events,
          filters: [],
          conversionWindowUnit: aiQuery.conversionTime ? aiQuery.conversionTime.unit : ConversionWindowUnit.Day,
          conversionWindowDuration: aiQuery.conversionTime ? aiQuery.conversionTime.value : 7,
          funnelOrder: FunnelOrder.Loose,
          exclusions: [],
          aggregateBy: [],
          behaviors: aiQuery.metrics.map((metric) => {
            const event = getCommonEventParams(metric);

            return {
              type: event.custom ? MetricType.CustomEvent : MetricType.Event,
              id: event.id,
              name: event.value,
              funnelOrder: FunnelOrder.Loose,
              filtersDeterminer: `all`,
              filters: getPropertyFilters(metric.filters).map((filter) => FilterClause.fromPropertyFilter(filter)),
            };
          }),
        },
        measurement: {
          math: measurementType,
          property: measurementProperty,
          stepIndex: null,
        },
        // auto expand for better visibility
        isExpanded: true,
      }),
    ];
  } else if (isRetentionQuery(aiQuery)) {
    // duplicate first metric if AI doesn't pick 2 for some reason
    const bornMetric = aiQuery.metrics[0];
    const metric = aiQuery.metrics[1] || aiQuery.metrics[0];

    const getBehavior = (aiEvent): SubBehavior => {
      const event = getCommonEventParams(aiEvent);

      return {
        type: event.custom ? MetricType.CustomEvent : MetricType.Event,
        id: event.id,
        name: event.value,
        filtersDeterminer: `all`,
        filters: getPropertyFilters(aiEvent.filters).map((filter) => FilterClause.fromPropertyFilter(filter)),
      };
    };

    report.sections.show.clauses = [
      new BehaviorShowClause({
        behavior: {
          type: MetricType.Retention,
          resourceType: InsightsResourceType.Events,
          filters: [],
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionType: `birth`,
          retentionUnboundedMode: `none`,
          retentionUnit: aiQuery.retentionUnit,
          behaviors: [getBehavior(bornMetric), getBehavior(metric)],
        },
        measurement: {
          math: RETENTION_MATH_TYPES.RETENTION_RATE,
          retentionBucketIndex: 0,
          retentionSegmentationEvent: getCommonEventParams(metric).value,
        },
        // auto expand for better visibility
        isExpanded: true,
      }),
    ];
  } else {
    report.sections.show.clauses = aiQuery.metrics.map((metric) => {
      const event = getCommonEventParams(metric);

      let perUserAggregation;

      if (`mathPerUser` in metric.measurement) {
        perUserAggregation = metric.measurement.mathPerUser;
      } else if (metric.measurement.type === `frequency-per-user`) {
        perUserAggregation = PRIMITIVE_MATH_TYPES.TOTAL;
      }

      return new BehaviorShowClause({
        type: TopLevelMetricType.Metric,
        behavior: {
          type: event.custom ? MetricType.CustomEvent : MetricType.Event,
          id: event.id,
          name: !event.custom ? event.value : undefined,
          resourceType: InsightsResourceType.Events,
          filtersDeterminer: `all`,
          filters: getPropertyFilters(metric.filters).map((filter) => FilterClause.fromPropertyFilter(filter)),
        },
        measurement: {
          math: metric.measurement.math,
          property:
            `property` in metric.measurement
              ? {
                  dataset: DATASET_MIXPANEL,
                  name: metric.measurement.property.property,
                  defaultType: metric.measurement.property.type,
                  type: metric.measurement.property.type,
                  resourceType: InsightsResourceType.Events,
                }
              : undefined,
          perUserAggregation,
        },
        // naive: hide regular metrics if there are formulas too
        isHidden: !!aiQuery.formulas?.length,
      });
    });
  }

  // formulas
  if (`formulas` in aiQuery) {
    report.sections.show.clauses = [
      ...report.sections.show.clauses,
      ...(aiQuery.formulas?.map(
        (formula) =>
          new FormulaShowClause({
            name: formula.label,
            definition: formula.value,
            type: TopLevelMetricType.Formula,
            measurement: {
              rolling: null,
              cumulative: false,
            },
          }),
      ) || []),
    ];
  }

  // filters
  report.sections.filter.clauses = getPropertyFilters(aiQuery.filters).map((filter) =>
    FilterClause.fromPropertyFilter(filter),
  );

  // breakdowns
  const [, groupSection] = getSegmentSections(getBreakdownSegments(aiQuery));
  report.sections.group = groupSection;

  // chart type
  const stackedPrefix = `stacked-`;
  if (aiQuery.chartType.startsWith(stackedPrefix)) {
    report.displayOptions.chartType = aiQuery.chartType.substring(stackedPrefix.length) as InsightsChartType;
    report.displayOptions.plotStyle = ChartPlotStyle.Stacked;
  } else {
    report.displayOptions.chartType = (aiQuery.chartType as InsightsChartType) || ChartType.Bar;
    report.displayOptions.plotStyle = ChartPlotStyle.Standard;
  }

  if (isFunnelsQuery(aiQuery)) {
    report.displayOptions.chartType = ChartType.FunnelSteps;

    if (aiQuery.chartType === `trends`) {
      report.displayOptions.chartType = ChartType.Line;
    } else if (aiQuery.chartType === `time-to-convert`) {
      report.displayOptions.chartType = ChartType.Bar;
    }
  }

  if (isRetentionQuery(aiQuery)) {
    if (aiQuery.chartType === `retention-table`) {
      report.displayOptions.chartType = ChartType.RetentionCurve;
    } else if (aiQuery.chartType === `retention-trend`) {
      report.displayOptions.chartType = ChartType.Line;
    }
  }

  report.timeComparison = getTimeComparisonParam(aiQuery.timeComparison);

  report.liftComparison = getLiftComparisonParam(aiQuery.breakdowns, true);

  return {
    name: aiQuery.report.name,
    type: aiQuery.report.type,
    description: ``,
    params: getBookmarkParamsWithDateRange(report.toBookmarkData(), getDateRange(aiQuery)),
  };
}
